import React from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  TrendingUp, 
  PieChart, 
  Globe, 
  Layers,
  Zap,
  Database,
  Stethoscope
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { KPICards } from '@/components/KPICards';
import { 
  DemographicsChart, 
  CostAnalysisChart, 
  RegionalChart, 
  TrendChart 
} from '@/components/Charts';
import { 
  ThreeDScatterPlot, 
  ThreeDNetworkGraph, 
  ThreeDVolumeChart 
} from '@/components/3DVisualization';
import { useHealthcareData } from '@/hooks/useHealthcareData';
import { IMAGES } from '@/assets/images';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

export default function Dashboard() {
  const { 
    isLoading, 
    data, 
    kpis, 
    regionalStats 
  } = useHealthcareData();

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-8 p-8">
          <div className="flex flex-col gap-4">
            <Skeleton className="h-12 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 w-full rounded-xl" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-[400px] lg:col-span-2 rounded-xl" />
            <Skeleton className="h-[400px] rounded-xl" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="relative min-h-screen overflow-hidden">
        {/* Interactive Background Theme */}
        <div className="absolute inset-0 -z-10">
          <img 
            src={IMAGES.MEDICAL_BG_4} 
            alt="Medical Background" 
            className="w-full h-full object-cover opacity-10 blur-[2px]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/40 to-background" />
          <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none" 
               style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, var(--primary) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="relative z-10 p-6 lg:p-10 space-y-10 max-w-[1600px] mx-auto">
          {/* Header Section */}
          <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-2">
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 px-3 py-1 font-mono">
                  <Zap className="w-3 h-3 mr-1.5 fill-primary" />
                  LIVE ANALYTICS 2026
                </Badge>
                <span className="text-xs text-muted-foreground font-mono">v4.0.2 Stable</span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-foreground flex items-center gap-3">
                <Stethoscope className="text-primary w-10 h-10" />
                Clinical Intelligence <span className="text-primary">Nexus</span>
              </h1>
              <p className="text-muted-foreground mt-2 max-w-2xl">
                Advanced interactive 3D dashboard providing real-time synthesis of patient demographics, treatment efficacy, and financial risk profiles.
              </p>
            </motion.div>

            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">System Status</p>
                <p className="text-xs text-emerald-500 flex items-center justify-end gap-1">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  Synchronized
                </p>
              </div>
              <div className="h-10 w-px bg-border mx-2" />
              <button className="bg-primary text-primary-foreground px-6 py-2.5 rounded-full font-semibold shadow-lg shadow-primary/20 hover:scale-105 transition-all flex items-center gap-2">
                <Database className="w-4 h-4" />
                Export Dataset
              </button>
            </div>
          </header>

          {/* KPI Section */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <KPICards metrics={kpis} />
          </motion.section>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            
            {/* 3D Scatter Plot - The Hero Visual */}
            <Card className="xl:col-span-8 overflow-hidden bg-card/50 backdrop-blur-xl border-primary/10 shadow-2xl relative">
              <div className="p-6 border-b border-border/50 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <Layers className="w-5 h-5 text-primary" />
                    Multi-Dimensional Patient Clusters
                  </h3>
                  <p className="text-sm text-muted-foreground">3D correlation mapping: Age vs. BMI vs. Charges</p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary">Interactive</Badge>
                  <Badge variant="outline">WebGL 3D</Badge>
                </div>
              </div>
              <div className="h-[500px] w-full relative">
                <ThreeDScatterPlot data={data} height={500} interactive />
                <div className="absolute bottom-4 right-4 p-4 bg-background/60 backdrop-blur-md rounded-lg border border-border/50 text-[10px] font-mono grid grid-cols-2 gap-x-4 gap-y-1">
                  <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary" /> Low Risk</div>
                  <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-accent" /> Medium Risk</div>
                  <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-destructive" /> High Risk</div>
                  <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-chart-4" /> Outlier</div>
                </div>
              </div>
            </Card>

            {/* Patient Demographics */}
            <Card className="xl:col-span-4 bg-card/50 backdrop-blur-xl border-primary/10">
              <div className="p-6 border-b border-border/50">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-primary" />
                  Demographics Analysis
                </h3>
              </div>
              <div className="p-6 h-[440px]">
                <DemographicsChart data={data} title="Age & Gender Distribution" />
              </div>
            </Card>

            {/* Cost Analysis Trend */}
            <Card className="xl:col-span-7 bg-card/50 backdrop-blur-xl border-primary/10">
              <div className="p-6 border-b border-border/50">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Cost Projections & Expenditure
                </h3>
              </div>
              <div className="p-6 h-[350px]">
                <CostAnalysisChart data={data} title="Monthly Healthcare Charges" />
              </div>
            </Card>

            {/* Regional Risk Matrix */}
            <Card className="xl:col-span-5 bg-card/50 backdrop-blur-xl border-primary/10">
              <div className="p-6 border-b border-border/50">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  Regional Health Index
                </h3>
              </div>
              <div className="p-6 h-[350px]">
                <RegionalChart data={regionalStats} title="Geographic Performance Summary" />
              </div>
            </Card>

            {/* Supplemental 3D Visuals */}
            <div className="xl:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="bg-card/30 backdrop-blur-md border-primary/5 p-0 overflow-hidden group">
                <div className="absolute top-4 left-4 z-10">
                  <h4 className="font-bold text-lg">System Network Graph</h4>
                  <p className="text-xs text-muted-foreground">Patient-Provider relationship nodes</p>
                </div>
                <div className="h-[300px]">
                  <ThreeDNetworkGraph data={data} height={300} />
                </div>
              </Card>

              <Card className="bg-card/30 backdrop-blur-md border-primary/5 p-0 overflow-hidden">
                <div className="absolute top-4 left-4 z-10">
                  <h4 className="font-bold text-lg">Risk Volume Analysis</h4>
                  <p className="text-xs text-muted-foreground">Volumetric data distribution</p>
                </div>
                <div className="h-[300px]">
                  <ThreeDVolumeChart data={data} height={300} />
                </div>
              </Card>
            </div>

            {/* Treatment Efficacy (Trend) */}
            <Card className="xl:col-span-12 bg-primary/5 border-primary/20">
              <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    Longitudinal Outcome Trends
                  </h3>
                  <p className="text-sm text-muted-foreground">Aggregated patient recovery rates and treatment response over 24 months.</p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">+14.2%</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Efficacy Growth</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-500">92.4%</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Target Compliance</p>
                  </div>
                </div>
              </div>
              <div className="px-6 pb-6 h-[300px]">
                <TrendChart data={data} title="Efficacy Trend" />
              </div>
            </Card>

          </div>

          {/* Footer Info */}
          <footer className="pt-10 border-t border-border/40 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground font-mono">
            <p>© 2026 Healthcare Analytics Suite. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Mainframe Sync: 100%
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-primary" />
                API Latency: 14ms
              </span>
              <span>Last Update: 2026-02-17 14:04:51</span>
            </div>
          </footer>
        </div>
      </div>
    </Layout>
  );
}
